import { useState, useEffect, useMemo } from "react";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { useI18n } from "@project/lib/use-i18n";
import { HERO_IMAGES } from "@project/lib/stock-images";
import {
  GALLERY_BY_CATEGORY,
  GALLERY_CATEGORY_IDS,
  type GalleryCategoryId,
} from "@project/lib/gallery-images";
import { galleryCategoryService } from "@project/services/admin/gallery-category.service";
import { galleryService } from "@project/services/admin/gallery.service";
import type { GalleryCategory, GalleryItem } from "@project/types/gallery";
import { resolveMediaUrl } from "@project/lib/utils";
import { Loader2, ZoomIn } from "lucide-react";

const CATEGORY_LABEL_KEYS: Record<
  GalleryCategoryId,
  | "community.gallery.categories.festivalCelebrations"
  | "community.gallery.categories.gaushala"
  | "community.gallery.categories.derasar"
  | "community.gallery.categories.outdoorActivities"
> = {
  "festival-celebrations": "community.gallery.categories.festivalCelebrations",
  gaushala: "community.gallery.categories.gaushala",
  derasar: "community.gallery.categories.derasar",
  "outdoor-activities": "community.gallery.categories.outdoorActivities",
};

export default function GalleryPage() {
  const { t } = useI18n();
  const [apiCategories, setApiCategories] = useState<GalleryCategory[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [itemsByCategory, setItemsByCategory] = useState<
    Record<string, GalleryItem[]>
  >({});
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    title?: string;
  } | null>(null);

  // 1. Initial Load: Fetch active categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setInitialLoading(true);
        const catRes = await galleryCategoryService.listPage({
          page: 1,
          page_size: 100,
        });
        const activeCats = (catRes.data || []).filter(c => c.active !== false);
        setApiCategories(activeCats);
        if (activeCats.length > 0) {
          setSelectedTab(String(activeCats[0].id));
        } else {
          setSelectedTab(GALLERY_CATEGORY_IDS[0]);
        }
      } catch (err) {
        console.error("Failed to load gallery categories", err);
        setSelectedTab(GALLERY_CATEGORY_IDS[0]);
      } finally {
        setInitialLoading(false);
      }
    };
    loadCategories();
  }, []);

  // 2. Dynamic Fetch: Whenever selectedTab changes, fetch gallery items for that category ID
  useEffect(() => {
    if (!selectedTab) return;

    const isApiCategory = apiCategories.some(c => String(c.id) === selectedTab);
    if (!isApiCategory) return;

    const fetchCategoryItems = async () => {
      try {
        setCategoryLoading(true);
        const categoryId = isNaN(Number(selectedTab))
          ? selectedTab
          : Number(selectedTab);
        const itemRes = await galleryService.listPage({
          category: categoryId,
          page: 1,
          page_size: 100,
        });
        const activeItems = (itemRes.data || []).filter(i => i.active !== false);
        setItemsByCategory(prev => ({
          ...prev,
          [selectedTab]: activeItems,
        }));
      } catch (err) {
        console.error(`Failed to load gallery items for category ${selectedTab}`, err);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchCategoryItems();
  }, [selectedTab, apiCategories]);

  const hasApiCategories = apiCategories.length > 0;

  // Build the list of tabs
  const tabsList = useMemo(() => {
    if (apiCategories.length > 0) {
      return apiCategories.map(c => ({
        id: String(c.id),
        label: c.category_name,
      }));
    }
    return GALLERY_CATEGORY_IDS.map(catId => ({
      id: catId,
      label: t(CATEGORY_LABEL_KEYS[catId]),
    }));
  }, [apiCategories, t]);

  const currentTab = selectedTab || tabsList[0]?.id || "";

  return (
    <PageShell>
      <PageHero
        eyebrow={t("community.gallery.hero.eyebrow")}
        title={t("community.gallery.hero.title")}
        subtitle={t("community.gallery.hero.subtitle")}
        image={HERO_IMAGES.gallery}
      />
      <div className="mx-auto max-w-6xl px-4 py-8">
        {initialLoading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs
            value={currentTab}
            onValueChange={val => setSelectedTab(val)}
            className="space-y-10"
          >
            <div className="anim-fade-up flex justify-center">
              <div className="flex w-full max-w-4xl justify-center overflow-x-auto pb-1">
                <TabsList className="bg-muted/40 border-border/50 inline-flex h-auto w-max min-w-0 flex-nowrap justify-center gap-1 rounded-full border p-1.5">
                  {tabsList.map(tab => (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="hover:bg-background/70 rounded-full px-4 py-2 text-xs font-medium text-muted-foreground transition-all duration-200 hover:text-foreground data-[state=active]:bg-gold data-[state=active]:text-gold-foreground data-[state=active]:shadow-sm sm:px-5 sm:text-sm cursor-pointer"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            </div>

            {tabsList.map(tab => {
              if (hasApiCategories) {
                const items = itemsByCategory[tab.id] || [];

                return (
                  <TabsContent
                    key={tab.id}
                    value={tab.id}
                    className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                  >
                    {categoryLoading && currentTab === tab.id && !items.length ? (
                      <div className="flex min-h-[200px] items-center justify-center">
                        <Loader2 className="h-7 w-7 animate-spin text-primary" />
                      </div>
                    ) : items.length ? (
                      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {items.map((item, idx) => {
                          const src = resolveMediaUrl(
                            item.image_url || item.image
                          );
                          if (!src) return null;
                          return (
                            <figure
                              key={item.id}
                              style={{
                                animationDelay: `${Math.min(idx, 11) * 60}ms`,
                              }}
                              onClick={() =>
                                setSelectedImage({
                                  src,
                                  title: item.title,
                                })
                              }
                              className="anim-zoom-in group relative cursor-pointer overflow-hidden rounded-2xl border border-border bg-muted shadow-sm transition hover:shadow-md"
                            >
                              <img
                                src={src}
                                alt={item.title}
                                loading="lazy"
                                className="aspect-square w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/30 opacity-0 transition-opacity group-hover:opacity-100 flex items-center justify-center">
                                <span className="rounded-full bg-black/60 p-2.5 text-white backdrop-blur-xs">
                                  <ZoomIn className="h-5 w-5" />
                                </span>
                              </div>
                              {item.title && (
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-6 text-white">
                                  <p className="line-clamp-2 text-xs font-semibold">
                                    {item.title}
                                  </p>
                                </div>
                              )}
                            </figure>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="bg-muted/30 anim-fade-up rounded-2xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
                        {t("community.gallery.empty")}
                      </p>
                    )}
                  </TabsContent>
                );
              }

              // Fallback to static stock images
              const staticImages =
                GALLERY_BY_CATEGORY[tab.id as GalleryCategoryId] || [];

              return (
                <TabsContent
                  key={tab.id}
                  value={tab.id}
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  {staticImages.length ? (
                    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                      {staticImages.map((image, idx) => (
                        <figure
                          key={image.src}
                          style={{
                            animationDelay: `${Math.min(idx, 11) * 60}ms`,
                          }}
                          onClick={() =>
                            setSelectedImage({
                              src: image.src,
                              title: image.alt,
                            })
                          }
                          className="anim-zoom-in group relative cursor-pointer overflow-hidden rounded-2xl border border-border bg-muted shadow-sm transition hover:shadow-md"
                        >
                          <img
                            src={image.src}
                            alt={image.alt}
                            loading="lazy"
                            className="aspect-square w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/30 opacity-0 transition-opacity group-hover:opacity-100 flex items-center justify-center">
                            <span className="rounded-full bg-black/60 p-2.5 text-white backdrop-blur-xs">
                              <ZoomIn className="h-5 w-5" />
                            </span>
                          </div>
                        </figure>
                      ))}
                    </div>
                  ) : (
                    <p className="bg-muted/30 anim-fade-up rounded-2xl border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
                      {t("community.gallery.empty")}
                    </p>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        )}
      </div>

      {/* Full-view Lightbox Modal for User Side */}
      <Dialog
        open={!!selectedImage}
        onOpenChange={open => {
          if (!open) setSelectedImage(null);
        }}
      >
        <DialogContent className="max-w-4xl overflow-hidden rounded-3xl p-3 sm:p-5">
          <DialogHeader className="border-border/60 border-b px-2 pb-3">
            <DialogTitle className="truncate font-serif text-base font-bold text-foreground sm:text-lg">
              {selectedImage?.title || t("community.gallery.hero.title")}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div
              key={selectedImage.src}
              className="bg-muted/20 anim-zoom-in mt-3 flex max-h-[78vh] w-full items-center justify-center overflow-hidden rounded-2xl p-1"
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title || "Full view"}
                className="max-h-[72vh] w-auto max-w-full rounded-xl object-contain shadow-sm"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
